import styled from '@emotion/styled';
import { GetServerSidePropsContext } from 'next';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  TypeContributionPageTemplate,
  TypeContributionPageTemplateSkeleton,
  TypeImpactFundTemplateSkeleton,
} from '../../../@types/generated';
import Divider from '../../common-components/component-library/Divider';
import Image from '../../common-components/component-library/Image';
import GhostButton from '../../common-components/component-library/buttons/GhostButton';
import Carousel from '../../common-components/component-library/carousel/Carousel';
import Select from '../../common-components/component-library/forms/Select';
import { getColors } from '../../common-components/component-library/forms/TextInput/Base';
import Container from '../../common-components/component-library/layout/Container';
import StripeInlineWidget from '../../common-components/component-library/layout/HeroSection/widgets/StripeInlineWidget';
import Typography from '../../common-components/component-library/text/Typography';
import { transformMetaHeaderType } from '../../common-components/contentful-elements/MetaHeader';
import { getCategories, type Category } from '../../utils/api/category';
import { getEntries } from '../../utils/api/contentful';
import { getPartnerProfile } from '../../utils/api/partner';
import CheckoutContext from '../../utils/checkoutContext';
import { UrlError } from '../../utils/error';
import { useMediaQuery } from '../../utils/hooks/useMediaQuery';
import { breakpointQueries, breakpoints, colors, spacing } from '../../utils/styleguide';
import withAppProps, { WithPageAppProps } from '../../utils/withAppProps';
import TestimonialCard from '../../common-components/component-library/TestimonialCard';
import TrustedByLogoSection from '../../common-components/component-library/TrustedByLogoSection';
import { EVENT_NAMES, sendEvent } from '../../utils/analytics';
import { PaymentFlowTrackingContext } from '../../common-components/contentful-elements/contexts/PaymentFlowTrackingContext';
import { extractPlainTextFromRichText } from '../../utils/helpers/extractPlainTextToRichText';

const Skeleton = styled.div`
  height: 600px;
  background: ${colors.grey02};
  border-radius: 4px;

  ${breakpoints.desktop} {
    margin: ${spacing[5]}px;
  }
`;

const HeadlineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;

  ${breakpoints.desktop} {
    padding: 0 ${spacing[5]}px;
    gap: ${spacing[2]}px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${breakpoints.desktop} {
    padding: 0 ${spacing[5]}px;
    gap: ${spacing[2]}px;
  }
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;

  ${breakpoints.desktop} {
    flex-direction: row;
    padding: ${spacing[5]}px ${spacing[5]}px ${spacing[10]}px ${spacing[5]}px;
  }
`;

const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: ${colors.grey50};
  border-radius: 4px;
  border: 1px solid ${colors.grey02};
  overflow: hidden;
  margin: ${spacing[5]}px 0;

  ${breakpoints.desktop} {
    margin: 0;
    width: 50%;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 300px;
`;

const StyledImage = styled(Image)`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const CategoryDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing[4]}px;
  flex: auto;
  justify-content: space-between;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
`;

const CheckoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${breakpoints.desktop} {
    width: 50%;
  }
`;

const TrustedByWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;

  ${breakpoints.desktop} {
    padding: ${spacing[5]}px ${spacing[5]}px 0;
  }
`;

const TitleSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]}px;
  margin-bottom: ${spacing[4]}px;
`;

export type ContributionPageProps = TypeContributionPageTemplate['fields'] & {
  categories: Category[];
};
export default function ContributionPage(props: ContributionPageProps) {
  const {
    sectionTitle,
    sectionParagraph,
    paymentOption,
    trustedBySectionTitle,
    partnerLogos,
    partnerTestimonials,
    categories,
  } = props;

  const [isTabletOrLarger] = useMediaQuery(breakpointQueries.tablet);
  const [selectedCategory, setSelectedCategory] = useState(categories?.[0]);
  const paymentFlowId = useRef(uuidv4());

  const handleSelectCategory = (categoryId: string) => {
    const category = categories?.find((category) => category.id === categoryId);
    if (category) {
      setSelectedCategory(category);
      sendEvent({
        name: EVENT_NAMES.selectContributionCategory,
        properties: {
          'category id': category.id,
        },
      });
    }
  };

  const logosToShow = isTabletOrLarger
    ? partnerLogos?.map((logo) => ({
        link: logo?.fields.link ?? '',
        image: {
          url: logo?.fields.image?.fields.file?.url ?? '',
          fileName: logo?.fields.image?.fields.file?.fileName ?? '',
          alt: logo?.fields.image?.fields.description ?? logo?.fields.image?.fields.title ?? '',
        },
      }))
    : partnerLogos?.slice(0, 2).map((logo) => ({
        link: logo?.fields.link ?? '',
        image: {
          url: logo?.fields.image?.fields.file?.url ?? '',
          fileName: logo?.fields.image?.fields.file?.fileName ?? '',
          alt: logo?.fields.image?.fields.description ?? logo?.fields.image?.fields.title ?? '',
        },
      }));

  return (
    <Container backgroundColor={colors.white}>
      <ContentWrapper>
        <HeadlineWrapper>
          <Typography variant="h1">{sectionTitle}</Typography>
          <Typography variant="body">{sectionParagraph}</Typography>
        </HeadlineWrapper>

        {selectedCategory && paymentOption && categories ? (
          <MainContentWrapper>
            <CategoryWrapper>
              <ImageWrapper>
                <StyledImage
                  url={selectedCategory?.image?.file?.url ?? ''}
                  fileName={selectedCategory?.image?.file?.fileName ?? ''}
                  alt={selectedCategory.image?.description ?? ''}
                />
              </ImageWrapper>
              <CategoryDescriptionWrapper>
                <TextWrapper>
                  <Typography variant="overlineBold">{selectedCategory?.name}</Typography>
                  <Typography variant="h6">
                    {selectedCategory?.description?.[0]?.content ?? ''}
                  </Typography>
                </TextWrapper>
                <GhostButton
                  label="Read more"
                  trailingIconName="chevron-right"
                  color={colors.purple400}
                  hideUnderline
                  href={`/impact-funds/${selectedCategory?.slug}`}
                />
              </CategoryDescriptionWrapper>
            </CategoryWrapper>

            <Divider hideOnSmallDevice vertical css={{ margin: `0 ${spacing[9]}px` }} />

            <CheckoutWrapper>
              <CheckoutContext.Provider
                value={{
                  initiator: '',
                  campaign: selectedCategory.name,
                }}
              >
                <PaymentFlowTrackingContext.Provider
                  value={{
                    paymentFlowId: paymentFlowId.current,
                    contentfulLandingPageId: '',
                  }}
                >
                  <StripeInlineWidget
                    category={{
                      id: selectedCategory.id,
                      name: selectedCategory.title?.[0].content ?? '',
                      tags: [],
                    }}
                    paymentOption={paymentOption}
                    titleSection={
                      <TitleSectionWrapper>
                        <Typography variant="bodyBold">{paymentOption.fields.title}</Typography>
                        {categories.length && categories.length > 1 && (
                          <CategorySelector
                            categories={categories}
                            selectedCategory={selectedCategory}
                            handleSelectCategory={handleSelectCategory}
                          />
                        )}
                      </TitleSectionWrapper>
                    }
                  />
                </PaymentFlowTrackingContext.Provider>
              </CheckoutContext.Provider>
            </CheckoutWrapper>
          </MainContentWrapper>
        ) : (
          <Skeleton />
        )}

        <Divider hideOnDesktop css={{ margin: `${spacing[5]}px 0` }} />

        <TrustedByWrapper>
          <Typography variant="overlineBold" color={colors.blackSecondary}>
            {trustedBySectionTitle}
          </Typography>
          {partnerLogos?.length !== undefined && <TrustedByLogoSection logos={logosToShow} />}
          {partnerTestimonials?.length !== undefined && (
            <Carousel>
              {partnerTestimonials?.map((testimonial, index) => (
                <TestimonialCard key={index} testimonial={testimonial} />
              ))}
            </Carousel>
          )}
        </TrustedByWrapper>
      </ContentWrapper>
    </Container>
  );
}

function CategorySelector({
  categories,
  selectedCategory,
  handleSelectCategory,
}: {
  categories: Category[];
  selectedCategory: Category;
  handleSelectCategory: (categoryId: string) => void;
}) {
  const [hasFocus, setHasFocus] = useState(false);

  const labelLifted = hasFocus || !!selectedCategory;
  const disabled = !categories.length || categories.length === 1;

  const handleOnFocus = () => {
    setHasFocus(true);
  };

  useEffect(() => {
    if (!selectedCategory) {
      setHasFocus(false);
    }
  }, [selectedCategory]);

  const { labelColor } = getColors({
    disabled,
    labelLifted,
  });

  return (
    <div
      css={{
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        backgroundColor: disabled ? colors.grey02 : colors.white,
        borderRadius: 4,
      }}
    >
      <Select
        id="fund"
        label="Select fund"
        labelColor={labelColor}
        value={selectedCategory.id}
        onChange={(event) => {
          handleSelectCategory(event.currentTarget.value);
        }}
        hasFocus={hasFocus}
        onFocus={handleOnFocus}
        disabled={disabled}
      >
        {categories.map((category) => {
          return (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          );
        })}
      </Select>
    </div>
  );
}

export type ServerSideProps = {
  props: ContributionPageProps;
} & WithPageAppProps;

export async function getContributionPageServerSideProps(
  page: TypeContributionPageTemplate,
  ctx: GetServerSidePropsContext,
): Promise<ServerSideProps> {
  const profile = await getPartnerProfile({ id: 'default' });
  const categoryIds =
    profile?.availableCategoryIds && profile?.availableCategoryIds.length > 0
      ? profile?.availableCategoryIds
      : undefined;

  const [categories, { items: pages }] = await Promise.all([
    getCategories({
      id: categoryIds ?? undefined,
      status: ['ACTIVE'],
    }),
    getEntries<TypeImpactFundTemplateSkeleton>({
      content_type: 'impactFundTemplate',
      include: 10,
    }),
  ]);

  const fund = ctx.params?.fund;
  if (Array.isArray(fund) && fund.length > 1) {
    throw new UrlError('Not found', { status: 404 });
  }

  const categoriesWithSlug = categories.result.map((category) => ({
    ...category,
    slug:
      pages.find((page) => page.fields.impactFund?.sys.id === category.id)?.fields.slug ??
      category.slug,
    image: {
      file: pages.find((page) => page.fields.impactFund?.sys.id === category.id)?.fields.impactFund
        ?.fields.topMedia?.fields.imageLandscape?.fields.file,
    },
  }));

  if (fund && !categoriesWithSlug.find((category) => category.slug === fund[0])) {
    throw new UrlError(`No impact fund found for slug ${fund}`, { status: 404 });
  }

  const availableCategories =
    fund && fund.length === 1
      ? categoriesWithSlug.filter((category) => category.slug === fund[0])
      : categoriesWithSlug.sort((a, b) => a.name.localeCompare(b.name));

  return {
    props: {
      ...page.fields,
      categories: availableCategories,
    },
    pageAppProps: {
      metaHeader: transformMetaHeaderType({
        metaHeader: page.fields.metaHeader,
        overrides: fund
          ? {
              seoTitle:
                `Contribute to the ${extractPlainTextFromRichText(availableCategories[0].title)}` ||
                '',
              seoDescription: availableCategories[0].headline || '',
              socialTitle:
                `Contribute to the ${extractPlainTextFromRichText(availableCategories[0].title)}` ||
                '',
              socialDescription: availableCategories[0].headline || '',
            }
          : {},
      }),
    },
  };
}

export const getServerSideProps = withAppProps(async (ctx: GetServerSidePropsContext) => {
  const { items: contributionPages } = await getEntries<TypeContributionPageTemplateSkeleton>({
    content_type: 'contributionPageTemplate',
    include: 10,
  });
  const [page] = contributionPages;

  if (!page) throw new Error('Page not found');

  return await getContributionPageServerSideProps(page, ctx);
});
